
import React from 'react';
import { Language } from '../types';
import { translations } from '../utils/translations';

interface ServicesProps {
  language: Language;
}

export const Services: React.FC<ServicesProps> = ({ language }) => {
  const t = translations[language].services;

  const ServiceCard = ({ title, desc, index }: { title: string, desc: string, index: number }) => (
    <div className="group relative bg-white border-[3px] border-black min-h-[180px] h-fit max-h-[180px] hover:max-h-[1000px] overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] hover:bg-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:shadow-[10px_10px_0px_0px_#ccff00] hover:-translate-x-1 hover:-translate-y-1 cursor-none flex flex-col">
      
      {/* Background Number - Subtly present */}
      <div className="absolute top-2 right-4 font-display text-8xl font-black text-zinc-100 group-hover:text-acid/5 transition-all duration-700 pointer-events-none z-0 select-none">
        0{index + 1}
      </div>

      {/* Main Content Container */}
      <div className="relative z-10 h-full w-full p-6 md:p-8 flex flex-col">
        
        {/* Title Area - Always visible, compact initially */}
        <div className="transition-all duration-500 ease-in-out transform group-hover:origin-top-left">
          <h3 className="font-display font-black text-2xl md:text-3xl uppercase leading-none tracking-tighter text-black group-hover:text-acid transition-colors duration-300">
            {title}
          </h3>
          <div className="w-10 h-1.5 bg-acid mt-3 group-hover:w-full transition-all duration-700"></div>
        </div>

        {/* Description Area - Expands the container naturally */}
        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-150 mt-0 group-hover:mt-6">
          <p className="font-mono text-sm md:text-base leading-relaxed text-zinc-300">
            {desc}
          </p>
          
          {/* Visual Protocol Tag */}
          <div className="mt-6 flex items-center gap-3">
            <div className="flex gap-1">
              <div className="w-1.5 h-1.5 bg-acid rounded-full animate-pulse"></div>
              <div className="w-1.5 h-1.5 bg-zinc-800 rounded-full"></div>
              <div className="w-1.5 h-1.5 bg-zinc-800 rounded-full"></div>
            </div>
            <span className="font-mono text-[9px] text-zinc-500 uppercase tracking-widest">Protocol_Active</span>
          </div>
        </div>
      </div>

      {/* Decorative Corner Notch */}
      <div className="absolute top-0 right-0 w-10 h-10 bg-zinc-50 group-hover:bg-zinc-900 transition-colors duration-500 rotate-45 translate-x-5 -translate-y-5 border-b-[3px] border-black"></div>
      
      {/* Interaction Feedback Border */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-acid transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left"></div>
    </div>
  );

  return (
    <div className="pt-24 pb-20 px-6 md:px-12 min-h-screen bg-zinc-50 flex flex-col items-center">
      <div className="max-w-[1400px] w-full space-y-24 md:space-y-40">
        
        {/* --- CATEGORÍA 1: CINE Y SERIES --- */}
        <section id="cinema-services" className="relative">
          <div className="hidden xl:block absolute -left-20 top-0 h-full">
            <span className="font-mono text-[10px] text-zinc-400 uppercase tracking-[1em] rotate-90 origin-left inline-block whitespace-nowrap">
              STORYTELLING_PROTOCOLS
            </span>
          </div>

          <div className="mb-12 md:mb-16">
            <div className="inline-block bg-black text-white px-6 md:px-8 py-2 md:py-3 mb-6 shadow-[6px_6px_0px_0px_#b026ff]">
              <h2 className="font-display font-black text-3xl md:text-6xl uppercase tracking-tighter leading-none">
                {t.cinema.title}
              </h2>
            </div>
            <p className="font-mono text-zinc-600 text-sm md:text-xl max-w-3xl border-l-4 border-zinc-200 pl-6 md:pl-8">
              {t.cinema.description}
            </p>
          </div>

          {/* Se ajusta a lg:grid-cols-3 para manejar todos los elementos (sección dinámica) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 items-start">
            {t.cinema.items.map((item, index) => (
              <ServiceCard 
                key={index}
                index={index}
                title={item.title}
                desc={item.desc}
              />
            ))}
          </div>
        </section>

        {/* --- CATEGORÍA 2: PUBLICIDAD Y MARCAS --- */}
        <section className="relative">
          <div className="hidden xl:block absolute -left-20 top-0 h-full">
            <span className="font-mono text-[10px] text-zinc-400 uppercase tracking-[1em] rotate-90 origin-left inline-block whitespace-nowrap">
              CONTENT_STRATEGIES
            </span>
          </div>

          <div className="mb-12 md:mb-16 flex flex-col items-end text-right">
            <div className="inline-block bg-black text-white px-6 md:px-8 py-2 md:py-3 mb-6 shadow-[6px_6px_0px_0px_#b026ff]">
              <h2 className="font-display font-black text-3xl md:text-6xl uppercase tracking-tighter leading-none">
                {t.ads.title}
              </h2>
            </div>
            <p className="font-mono text-zinc-600 text-sm md:text-xl max-w-3xl border-r-4 border-zinc-200 pr-6 md:pr-8">
              {t.ads.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-start">
            {t.ads.items.map((item, index) => (
              <ServiceCard 
                key={index}
                index={index}
                title={item.title}
                desc={item.desc}
              />
            ))}
          </div>
        </section>

      </div>

      <div className="mt-32 md:mt-48 w-full flex flex-col items-center opacity-30">
        <div className="w-px h-24 md:h-32 bg-gradient-to-b from-black to-transparent"></div>
        <span className="font-mono text-[9px] md:text-[10px] mt-4 tracking-[0.5em] text-black">END_OF_SERVICES_PROTOCOL</span>
      </div>
    </div>
  );
};
